import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { trackPageView } from '@/lib/analytics'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { LogProvider } from '@/log/LogProvider'
import { ToastProvider } from '@/components/ui/Toast'
import { AppShell } from '@/components/AppShell'
import { FullScreenLoader } from '@/components/ui/Spinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SignIn } from '@/routes/SignIn'
import { Gate } from '@/routes/Gate'

const lazyRoute = <T extends object>(loader: () => Promise<T>, key: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[key] as unknown as React.ComponentType })))

const Log = lazyRoute(() => import('@/routes/Log'), 'Log')
const AddFood = lazyRoute(() => import('@/routes/AddFood'), 'AddFood')
const FoodDetail = lazyRoute(() => import('@/routes/FoodDetail'), 'FoodDetail')
const PresetEditor = lazyRoute(() => import('@/routes/PresetEditor'), 'PresetEditor')
const EntryForm = lazyRoute(() => import('@/routes/EntryForm'), 'EntryForm')
const AIEntry = lazyRoute(() => import('@/routes/AIEntry'), 'AIEntry')
const Progress = lazyRoute(() => import('@/routes/Progress'), 'Progress')
const Measurements = lazyRoute(() => import('@/routes/Measurements'), 'Measurements')
const Vitals = lazyRoute(() => import('@/routes/Vitals'), 'Vitals')
const Recipes = lazyRoute(() => import('@/routes/Recipes'), 'Recipes')
const RecipeDetail = lazyRoute(() => import('@/routes/RecipeDetail'), 'RecipeDetail')
const RecipeEditor = lazyRoute(() => import('@/routes/RecipeEditor'), 'RecipeEditor')
const Planner = lazyRoute(() => import('@/routes/Planner'), 'Planner')
const ShoppingList = lazyRoute(() => import('@/routes/ShoppingList'), 'ShoppingList')
const Challenge = lazyRoute(() => import('@/routes/Challenge'), 'Challenge')
const Tribe = lazyRoute(() => import('@/routes/Tribe'), 'Tribe')
const Learn = lazyRoute(() => import('@/routes/Learn'), 'Learn')
const LearnArticle = lazyRoute(() => import('@/routes/Learn'), 'LearnArticle')
const Settings = lazyRoute(() => import('@/routes/Settings'), 'Settings')

function PublicOnly() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from || '/log'} replace />
  }
  return <Outlet />
}

function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

function RequirePremium() {
  const { user, loading, access, profileMissing, needsOnboarding } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  if (profileMissing || needsOnboarding) return <Navigate to="/upgrade" replace />
  if (access === 'checking') return <FullScreenLoader label="Checking your subscription…" />
  if (access === 'denied') return <Navigate to="/upgrade" replace />
  return (
    <LogProvider>
      <AppShell>
        <Suspense fallback={<FullScreenLoader />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </LogProvider>
  )
}

function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <BrowserRouter>
              <PageViewTracker />
              <Routes>
                <Route element={<PublicOnly />}>
                  <Route path="/sign-in" element={<SignIn />} />
                </Route>
                <Route element={<RequireAuth />}>
                  <Route path="/upgrade" element={<Gate />} />
                </Route>
                <Route element={<RequirePremium />}>
                  <Route path="/log" element={<Log />} />
                  <Route path="/log/add" element={<AddFood />} />
                  <Route path="/log/food/:source/:id" element={<FoodDetail />} />
                  <Route path="/log/my-food/:id" element={<PresetEditor />} />
                  <Route path="/log/my-food/:id/edit" element={<PresetEditor />} />
                  <Route path="/log/entry/:kind" element={<EntryForm />} />
                  <Route path="/log/ai/:mode" element={<AIEntry />} />
                  <Route path="/progress" element={<Progress />} />
                  <Route path="/measurements" element={<Measurements />} />
                  <Route path="/vitals" element={<Vitals />} />
                  <Route path="/recipes" element={<Recipes />} />
                  <Route path="/recipes/mine/new" element={<RecipeEditor />} />
                  <Route path="/recipes/mine/:id/edit" element={<RecipeEditor />} />
                  <Route path="/recipes/:id" element={<RecipeDetail />} />
                  <Route path="/planner" element={<Planner />} />
                  <Route path="/planner/shopping" element={<ShoppingList />} />
                  <Route path="/challenge" element={<Challenge />} />
                  <Route path="/challenge/tribe" element={<Tribe />} />
                  <Route path="/learn" element={<Learn />} />
                  <Route path="/learn/:id" element={<LearnArticle />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
                <Route path="/" element={<Navigate to="/log" replace />} />
                <Route path="*" element={<Navigate to="/log" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
